import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classnames from 'classnames'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap'
import { setTenantRenewalsDisabled } from '../../actions/cspRenewalsActions'
import TenantSubscriptionsTab from './TenantSubscriptionsTab'

// Locked-by-default editor pattern: admin sees current state as static text,
// must click Edit then Save... then Confirm on a modal showing the explicit
// consequence wording. Same shape as the License Management MPNID editor -
// admin learns the pattern once.

const formatTimestamp = (iso) => {
  if (!iso) return null
  return new Date(iso).toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

const TenantSettingsDrawer = ({ isOpen, toggle, tenantId, isAdmin, includeInactive }) => {
  const dispatch = useDispatch()
  const tenant = useSelector((state) =>
    (state.cspRenewals.tenants || []).find((t) => t.tenantId === tenantId)
  )
  const updatingTenantId = useSelector((state) => state.cspRenewals.updatingTenantId)
  const isUpdating = updatingTenantId === tenantId

  const [editing, setEditing] = useState(false)
  const [pendingDisabled, setPendingDisabled] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('settings')

  // Reset edit + tab state every time the drawer opens or the tenant changes.
  // Settings is the default landing tab; admin clicks Subscriptions to see
  // the per-tenant sub list.
  useEffect(() => {
    if (isOpen) {
      setEditing(false)
      setPendingDisabled(false)
      setConfirmOpen(false)
      setActiveTab('settings')
    }
  }, [isOpen, tenantId])

  if (!tenant) return null

  const currentlyDisabled = !!tenant.renewalsDisabled
  const disabledAt = formatTimestamp(tenant.renewalsDisabledAt)
  const disabledBy = tenant.renewalsDisabledByUpn

  const startEdit = () => {
    setPendingDisabled(!currentlyDisabled)
    setEditing(true)
  }

  const cancelEdit = () => {
    setEditing(false)
    setPendingDisabled(false)
  }

  const openConfirm = () => {
    if (pendingDisabled === currentlyDisabled) {
      // No change to confirm. Just exit edit mode.
      cancelEdit()
      return
    }
    setConfirmOpen(true)
  }

  const applyChange = async () => {
    setConfirmOpen(false)
    await dispatch(setTenantRenewalsDisabled(tenant.tenantId, pendingDisabled, includeInactive))
    setEditing(false)
  }

  const willDisable = pendingDisabled
  const confirmTitle = willDisable ? 'Disable renewal emails' : 'Re-enable renewal emails'
  const confirmBody = willDisable ? (
    <>
      <p>
        You are about to <strong>disable</strong> renewal emails for{' '}
        <strong>{tenant.companyName || tenant.tenantName}</strong>.
      </p>
      <p>
        No client emails and no BCC alerts will be sent for this tenant until
        renewals are re-enabled. This affects both yearly and monthly tracks
        across every upcoming renewal window.
      </p>
    </>
  ) : (
    <>
      <p>
        You are about to <strong>re-enable</strong> renewal emails for{' '}
        <strong>{tenant.companyName || tenant.tenantName}</strong>.
      </p>
      <p>
        The next dispatcher run will resume normal sends to the configured
        recipients.
      </p>
    </>
  )

  return (
    <>
      {/* 75vw modal so the Subscriptions tab's table has real estate to
          breathe without horizontal scroll. style overrides bootstrap's
          .modal-md max-width=500px default. */}
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        size="lg"
        style={{ maxWidth: '75vw', width: '75vw' }}
      >
        <ModalHeader toggle={toggle}>
          Tenant Settings
          <div className="small text-muted mt-1" style={{ fontWeight: 400 }}>
            {tenant.companyName || tenant.tenantName}
          </div>
        </ModalHeader>
        <ModalBody>
          <Nav tabs className="mb-3">
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === 'settings' })}
                onClick={() => setActiveTab('settings')}
                style={{ cursor: 'pointer' }}
              >
                Settings
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === 'subscriptions' })}
                onClick={() => setActiveTab('subscriptions')}
                style={{ cursor: 'pointer' }}
              >
                Subscriptions
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="settings">
              {/* ─── Renewal emails toggle ───────────────────────────────── */}
              <div className="mb-4">
                <div className="text-uppercase text-muted small font-weight-bold mb-2" style={{ letterSpacing: 0.5 }}>
                  Renewal emails
                </div>

                {!editing ? (
                  <div>
                    <div className="d-flex align-items-center">
                      {currentlyDisabled ? (
                        <Badge color="danger" className="mr-2">Disabled</Badge>
                      ) : (
                        <Badge color="success" className="mr-2">Enabled</Badge>
                      )}
                      {isAdmin && (
                        <Button
                          color="link"
                          size="sm"
                          className="p-0 ml-2"
                          disabled={isUpdating}
                          onClick={startEdit}
                        >
                          Edit
                        </Button>
                      )}
                    </div>
                    {currentlyDisabled && (disabledBy || disabledAt) && (
                      <div className="small text-muted mt-1">
                        Disabled
                        {disabledBy && <> by <strong>{disabledBy}</strong></>}
                        {disabledAt && <> on {disabledAt}</>}.
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div>
                      <label className="d-block mb-1">
                        <input
                          type="radio"
                          className="mr-2"
                          checked={pendingDisabled === false}
                          onChange={() => setPendingDisabled(false)}
                        />
                        Enabled
                      </label>
                      <label className="d-block mb-1">
                        <input
                          type="radio"
                          className="mr-2"
                          checked={pendingDisabled === true}
                          onChange={() => setPendingDisabled(true)}
                        />
                        Disabled
                      </label>
                    </div>
                    <div className="mt-2">
                      <Button
                        size="sm"
                        color="outline-secondary"
                        className="mr-2"
                        onClick={cancelEdit}
                        disabled={isUpdating}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        color="primary"
                        onClick={openConfirm}
                        disabled={isUpdating || pendingDisabled === currentlyDisabled}
                      >
                        Save…
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* ─── Tenant identity (read-only) ─────────────────────────── */}
              <div className="mb-2">
                <div className="text-uppercase text-muted small font-weight-bold mb-2" style={{ letterSpacing: 0.5 }}>
                  Identity
                </div>
                <div className="small">
                  <div>DD TenantId: <code>{tenant.tenantId}</code></div>
                  {tenant.domainPrefix && <div>Domain: <code>{tenant.domainPrefix}</code></div>}
                  <div>Mapping: {tenant.companyName ? <>{tenant.companyName} {tenant.isManuallyMapped && <Badge color="warning" className="ml-1">Override</Badge>}</> : <em className="text-muted">Unmapped</em>}</div>
                </div>
              </div>
            </TabPane>

            <TabPane tabId="subscriptions">
              <TenantSubscriptionsTab
                isOpen={isOpen && activeTab === 'subscriptions'}
                tenantId={tenantId}
              />
            </TabPane>
          </TabContent>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Hard-confirm modal layered on top */}
      <Modal isOpen={confirmOpen} toggle={() => setConfirmOpen(false)} size="md">
        <ModalHeader toggle={() => setConfirmOpen(false)}>
          {confirmTitle}
        </ModalHeader>
        <ModalBody>
          {confirmBody}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setConfirmOpen(false)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button color={willDisable ? 'danger' : 'primary'} onClick={applyChange} disabled={isUpdating}>
            {willDisable ? 'Confirm Disable' : 'Confirm Enable'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default TenantSettingsDrawer
